import React, { useEffect, useState } from 'react';
import { X, Package, User, PlayCircle, Truck as TruckIcon, MapPin, CheckCircle2, FileText, Clock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { tripsApi } from '../lib/api';

const eventMeta = {
  posted:      { icon: Package, color: 'bg-blue-50 text-blue-600', title: 'Load Posted' },
  booked:      { icon: User, color: 'bg-purple-50 text-purple-600', title: 'Booked by Driver' },
  loading:     { icon: PlayCircle, color: 'bg-[#FFB800]/15 text-[#B37800]', title: 'Loading Started' },
  in_transit:  { icon: TruckIcon, color: 'bg-[#00A651]/10 text-[#00A651]', title: 'In Transit' },
  arrived:     { icon: MapPin, color: 'bg-[#00A651]/10 text-[#00A651]', title: 'Arrived at Destination' },
  delivered:   { icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-600', title: 'Delivered (PoD Uploaded)' },
  invoiced:    { icon: FileText, color: 'bg-[#0B2545]/10 text-[#0B2545]', title: 'Invoice Generated' },
};

// Fallback demo events when no events found for a load
const demoEvents = (load) => {
  if (!load) return [];
  const now = Date.now();
  return [
    { id: 'e1', event: 'posted',     actor: load.poster || 'Shipper',       note: `Posted at ₹${load.rate?.toLocaleString('en-IN')}`, at: new Date(now - 5*3600000).toISOString() },
    { id: 'e2', event: 'booked',     actor: load.driver || 'Sohan Lal',      note: 'Driver accepted the trip',                             at: new Date(now - 4.5*3600000).toISOString() },
    { id: 'e3', event: 'loading',    actor: load.driver || 'Sohan Lal',      note: `Loading at ${load.origin?.split(',')[0]}`,               at: new Date(now - 3.5*3600000).toISOString() },
    { id: 'e4', event: 'in_transit', actor: load.driver || 'Sohan Lal',      note: 'Crossed Nashik toll',                                    at: new Date(now - 2*3600000).toISOString() },
    ...(load.status === 'Delivered' ? [
      { id: 'e5', event: 'delivered', actor: load.driver || 'Sohan Lal',     note: `Received by ${load.recipient || 'Ramesh Kumar'}`,        at: new Date(now - 30*60000).toISOString() },
      { id: 'e6', event: 'invoiced',  actor: 'System',                        note: `INV/2026/${Math.random().toString(36).slice(2,8).toUpperCase()} generated`, at: new Date(now - 20*60000).toISOString() },
    ] : []),
  ];
};

const fmt = (ts) => {
  const d = new Date(ts);
  return { time: d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }), date: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) };
};

export default function TripTimeline({ open, onClose, load }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !load) return;
    setLoading(true);
    tripsApi.timeline(load.id).then(r => {
      if (r && r.length > 0) setEvents(r);
      else setEvents(demoEvents(load));
    }).catch(() => setEvents(demoEvents(load))).finally(() => setLoading(false));
  }, [open, load]);

  if (!open || !load) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-[#0B2545]">Trip Timeline</h3>
            <p className="text-xs text-gray-500 font-mono">{load.id} • {load.origin?.split(',')[0]} → {load.destination?.split(',')[0]}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="py-8 text-center text-gray-400"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
          ) : events.length === 0 ? (
            <div className="py-8 text-center text-gray-400"><Clock className="w-8 h-8 mx-auto mb-2" /><p>No events yet</p></div>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-gray-200"></div>
              {events.map((ev, i) => {
                const meta = eventMeta[ev.event] || { icon: Clock, color: 'bg-gray-100 text-gray-500', title: ev.event };
                const Icon = meta.icon;
                const t = fmt(ev.at);
                const isLast = i === events.length - 1;
                return (
                  <div key={ev.id || i} className="flex gap-4 relative">
                    <div className={`w-10 h-10 rounded-full ${meta.color} flex items-center justify-center flex-shrink-0 border-4 border-white z-10`}>
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <div className={`flex-1 ${isLast ? '' : 'pb-5'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-[#0B2545] text-sm">{meta.title}</p>
                          {ev.note && <p className="text-xs text-gray-600 mt-0.5">{ev.note}</p>}
                          {ev.actor && <p className="text-[10px] text-gray-400 mt-0.5">by {ev.actor}</p>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs font-semibold text-[#00A651]">{t.time}</p>
                          <p className="text-[10px] text-gray-400">{t.date}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500 uppercase">Events</p>
              <p className="font-bold text-[#0B2545]">{events.length}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500 uppercase">Status</p>
              <p className="font-bold text-[#00A651] text-xs">{events[events.length-1]?.event || 'Pending'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500 uppercase">Duration</p>
              <p className="font-bold text-[#0B2545] text-xs">{events.length > 1 ? Math.round((new Date(events[events.length-1].at) - new Date(events[0].at)) / 3600000) + 'h' : '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
